import React, { useMemo } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    LinearProgress,
    Stack,
    Avatar,
    Divider,
    Tooltip
} from '@mui/material';
import { RecordVoiceOver, InfoOutlined } from '@mui/icons-material';

const getSpeakerColor = (speakerId) => {
    if (speakerId === null) return '#757575';
    const colors = [
        '#1976d2', '#e53935', '#43a047', '#7b1fa2',
        '#ff9800', '#0097a7', '#c2185b', '#827717',
        '#3949ab', '#6d4c41',
    ];
    const hash = Math.abs(
        speakerId.toString().split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
    );
    return colors[hash % colors.length];
};

const getSpeakerInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const formatDuration = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    if (m === 0) return `${s}վ`;
    return `${m}ր ${s}վ`;
};

const TalkTimeAnalysis = ({ diarizationSegments, speakerNames }) => {
    const stats = useMemo(() => {
        if (!diarizationSegments || !Array.isArray(diarizationSegments) || diarizationSegments.length === 0) {
            return null;
        }

        const speakerStats = {};
        let totalDuration = 0;

        diarizationSegments.forEach(segment => {
            const duration = segment.end - segment.start;
            const speakerId = segment.speaker;

            if (!speakerStats[speakerId]) {
                speakerStats[speakerId] = {
                    duration: 0,
                    speakerId
                };
            }
            speakerStats[speakerId].duration += duration;
            totalDuration += duration;
        });

        const result = Object.values(speakerStats)
            .sort((a, b) => b.duration - a.duration)
            .map(stat => ({
                ...stat,
                percentage: totalDuration > 0 ? (stat.duration / totalDuration) * 100 : 0
            }));

        return { speakers: result, totalDuration };
    }, [diarizationSegments]);

    if (!stats) return null;

    return (
        <Card elevation={3} sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <RecordVoiceOver color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Խոսակցության ժամանակ
                    </Typography>
                    <Tooltip
                        title="Տոկոսները հաշվարկվում են ըստ խոսքի ընդհանուր տևողության՝ առանց լռության, և ոչ ըստ ձայնագրության ընդհանուր տևողության։ Միաժամանակյա խոսելիս ժամանակը հաշվարկվում է բոլոր խոսողների համար առանձին առանձին։"
                        arrow
                        placement="top"
                    >
                        <InfoOutlined color="action" sx={{ fontSize: 20, cursor: 'help', opacity: 0.7 }} />
                    </Tooltip>
                </Stack>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={3}>
                    {stats.speakers.map((speaker) => {
                        const name = speakerNames && speakerNames[speaker.speakerId]
                            ? speakerNames[speaker.speakerId]
                            : `Խոսող ${parseInt(speaker.speakerId) + 1}`;
                        const color = getSpeakerColor(speaker.speakerId);

                        return (
                            <Box key={speaker.speakerId}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Avatar sx={{ bgcolor: color, width: 24, height: 24, fontSize: '0.75rem' }}>
                                            {getSpeakerInitials(name)}
                                        </Avatar>
                                        <Typography variant="body1" fontWeight="500">
                                            {name}
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Typography variant="body2" color="text.secondary">
                                            {formatDuration(speaker.duration)}
                                        </Typography>
                                        <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 40, textAlign: 'right' }}>
                                            {Math.round(speaker.percentage)}%
                                        </Typography>
                                    </Stack>
                                </Stack>
                                <LinearProgress
                                    variant="determinate"
                                    value={speaker.percentage}
                                    sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        bgcolor: `${color}20`, // Light version of speaker color as background
                                        '& .MuiLinearProgress-bar': {
                                            bgcolor: color,
                                            borderRadius: 4
                                        }
                                    }}
                                />
                            </Box>
                        );
                    })}
                </Stack>
            </CardContent>
        </Card>
    );
};

export default TalkTimeAnalysis;
